// Typography rules (see src/design-system/TYPOGRAPHY_AND_THEME.md):
//   - Every title uses a typography.h* preset, OR `font-heading font-bold` directly.
//   - Body text inherits --font-body automatically; do not set fontFamily.
//   - Brand wordmark (myvitae.ai) keeps Quicksand via logoFontStyle — don't touch.
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardBody,
  CardHeader,
  ChevronDownIcon,
  ChevronUpIcon,
  FormField,
  SecondaryButton,
  Select,
  StatusBadge,
} from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import { useAdminAllAutomationRuns } from "../../hooks/useAdminAutomations";
import RunFunnel, { runToFunnelCounts } from "./RunFunnel";
import { formatCount, formatDateTime, formatRelativeTime, getRunStatusMeta } from "./automationUtils";

const PAGE_SIZE = 10;

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "running", label: "Running" },
];

const KIND_OPTIONS = [
  { value: "", label: "Real + dry runs" },
  { value: "false", label: "Real runs only" },
  { value: "true", label: "Dry runs only" },
];

const RunKindBadges = ({ run }) => (
  <span className="inline-flex items-center gap-1.5 flex-wrap">
    <StatusBadge size="sm" variant={getRunStatusMeta(run.status).tone}>
      {getRunStatusMeta(run.status).label}
    </StatusBadge>
    {run.dry_run ? (
      <StatusBadge size="sm" variant="neutral" title="Dry run — the funnel was evaluated but nothing was enqueued">
        Dry run
      </StatusBadge>
    ) : null}
  </span>
);

RunKindBadges.propTypes = { run: PropTypes.object.isRequired };

const RunDuration = ({ run }) => {
  if (!run?.started_at || !run?.finished_at) return <span className={textColors.muted}>—</span>;
  const ms = new Date(run.finished_at).getTime() - new Date(run.started_at).getTime();
  if (!Number.isFinite(ms) || ms < 0) return <span className={textColors.muted}>—</span>;
  if (ms < 1000) return <>{ms} ms</>;
  if (ms < 60 * 1000) return <>{(ms / 1000).toFixed(1)} s</>;
  return <>{Math.round(ms / 60000)} min</>;
};

RunDuration.propTypes = { run: PropTypes.object };

const AffectedUsersCell = ({ run }) => {
  const count = Number(run.affected_user_count || 0);
  if (count <= 0) {
    return <span className={`${typography.bodySmall} ${textColors.muted}`}>0</span>;
  }
  return (
    <span
      className={`${typography.bodySmall} font-semibold tabular-nums text-green-700 dark:text-green-300`}
      title={`${formatCount(count)} distinct user${count === 1 ? "" : "s"} received at least one message from this run`}
    >
      {formatCount(count)} user{count === 1 ? "" : "s"}
    </span>
  );
};

AffectedUsersCell.propTypes = { run: PropTypes.object.isRequired };

const Pagination = ({ page, pageSize, total, count, hasMore, fetching, onPage }) => {
  const first = total === 0 || count === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = first === 0 ? 0 : first + count - 1;
  const pages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <p className={`${typography.caption} ${textColors.tertiary}`} aria-live="polite">
        Showing {formatCount(first)}–{formatCount(last)} of {formatCount(total)}
        {fetching ? " · updating…" : ""}
      </p>
      <div className="flex items-center gap-2">
        <SecondaryButton size="sm" onClick={() => onPage(page - 1)} disabled={page <= 1 || fetching}>
          Previous
        </SecondaryButton>
        <span className={`${typography.caption} ${textColors.secondary} tabular-nums whitespace-nowrap`}>
          Page {formatCount(page)} of {formatCount(pages)}
        </span>
        <SecondaryButton size="sm" onClick={() => onPage(page + 1)} disabled={!hasMore || fetching}>
          Next
        </SecondaryButton>
      </div>
    </div>
  );
};

Pagination.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  hasMore: PropTypes.bool,
  fetching: PropTypes.bool,
  onPage: PropTypes.func.isRequired,
};

/**
 * Cross-automation run history: every recorded AutomationRun (any automation,
 * newest first), with the distinct "affected users" count per run — the
 * answer to "which automations ran, when, and how many users did each one
 * actually touch". Complements the per-automation "View runs" drawer, which
 * only shows one automation at a time.
 */
const AutomationRunHistorySection = ({ automationOptions = [], defaultOpen = true, className = "" }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [page, setPage] = useState(1);
  const [automationId, setAutomationId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dryRunFilter, setDryRunFilter] = useState("");

  const filters = {
    automation_id: automationId || undefined,
    status: statusFilter || undefined,
    dry_run: dryRunFilter || undefined,
  };
  const query = useAdminAllAutomationRuns({ page, pageSize: PAGE_SIZE, filters, enabled: open });
  const runs = Array.isArray(query.data?.results) ? query.data.results : [];
  const total = Number(query.data?.total || 0);
  const hasMore = Boolean(query.data?.has_more);

  const updateFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  return (
    <Card variant="glass" className={className}>
      <CardHeader
        title="Run history"
        subtitle={
          open
            ? "Every automation run, across every automation — newest first, with how many users each run actually affected."
            : "Every recorded run across every automation."
        }
        actions={
          <SecondaryButton
            type="button"
            size="sm"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="automation-run-history-panel"
            rightIcon={open ? <ChevronUpIcon size="sm" /> : <ChevronDownIcon size="sm" />}
          >
            {open ? "Hide" : "Show"}
          </SecondaryButton>
        }
      />

      {open ? (
        <CardBody id="automation-run-history-panel" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FormField label="Automation" htmlFor="run-history-automation">
              <Select
                id="run-history-automation"
                value={automationId}
                onChange={(e) => updateFilter(setAutomationId)(e.target.value)}
                options={[
                  { value: "", label: "All automations" },
                  ...automationOptions.map((a) => ({ value: String(a.id), label: a.name })),
                ]}
                placeholder={null}
              />
            </FormField>
            <FormField label="Status" htmlFor="run-history-status">
              <Select
                id="run-history-status"
                value={statusFilter}
                onChange={(e) => updateFilter(setStatusFilter)(e.target.value)}
                options={STATUS_OPTIONS}
                placeholder={null}
              />
            </FormField>
            <FormField label="Kind" htmlFor="run-history-kind">
              <Select
                id="run-history-kind"
                value={dryRunFilter}
                onChange={(e) => updateFilter(setDryRunFilter)(e.target.value)}
                options={KIND_OPTIONS}
                placeholder={null}
              />
            </FormField>
          </div>

          {query.error ? (
            <div className="rounded-2xl px-4 py-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800" role="alert">
              <p className={`${typography.bodySmall} text-red-800 dark:text-red-300`}>
                Failed to load run history: {query.error?.message || "Unknown error"}
              </p>
              <div className="mt-2">
                <SecondaryButton size="sm" onClick={() => query.refetch()}>
                  Retry
                </SecondaryButton>
              </div>
            </div>
          ) : query.isLoading ? (
            <div className="space-y-3" aria-busy="true" aria-live="polite">
              {[0, 1, 2].map((i) => (
                <div key={i} className={`rounded-2xl border ${borders.default} ${surfaces.primary} p-4`}>
                  <div className="animate-pulse space-y-2">
                    <div className={`h-3 ${surfaces.tertiary} rounded w-1/2`} />
                    <div className={`h-3 ${surfaces.tertiary} rounded w-3/4`} />
                  </div>
                </div>
              ))}
            </div>
          ) : runs.length === 0 ? (
            <div className={`rounded-2xl border border-dashed ${borders.default} ${surfaces.secondary} px-4 py-8 text-center`}>
              <h3 className={`${typography.h5} ${textColors.primary}`}>No runs yet</h3>
              <p className={`mt-1 ${typography.bodySmall} ${textColors.tertiary}`}>
                {automationId || statusFilter || dryRunFilter
                  ? "No runs match these filters."
                  : "Once an automation runs (on its schedule or via “Run now”), it shows up here with how many users it affected."}
              </p>
            </div>
          ) : (
            <>
              {/* Mobile: cards */}
              <ol className="space-y-3 sm:hidden">
                {runs.map((run) => (
                  <li key={run.id} className={`rounded-2xl border ${borders.default} ${surfaces.primary} p-4`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className={`${typography.bodySmall} ${textColors.primary} font-medium truncate`}>
                          {run.automation?.name || "Deleted automation"}
                        </div>
                        <div className={`${typography.caption} ${textColors.tertiary}`}>
                          {formatRelativeTime(run.started_at)}
                          {run.trigger_type === "event" ? ` · Event: ${run.trigger_event_label || run.trigger_event}` : ""}
                        </div>
                      </div>
                      <RunKindBadges run={run} />
                    </div>
                    <div className="mt-3">
                      <RunFunnel counts={runToFunnelCounts(run)} mode="run" variant="full" />
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className={`${typography.caption} ${textColors.tertiary}`}>
                        Duration: <RunDuration run={run} />
                      </span>
                      <AffectedUsersCell run={run} />
                    </div>
                    {run.error ? (
                      <p className={`mt-2 ${typography.caption} text-red-700 dark:text-red-300 break-words`}>{run.error}</p>
                    ) : null}
                  </li>
                ))}
              </ol>

              {/* Tablet+: table */}
              <div className={`hidden sm:block rounded-2xl border ${borders.default} overflow-hidden`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left" aria-label="Run history">
                    <thead className={`${surfaces.secondary} border-b ${borders.default}`}>
                      <tr className={`${typography.caption} ${textColors.secondary}`}>
                        <th className="px-3 py-2 font-medium">Automation</th>
                        <th className="px-3 py-2 font-medium">Started</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap" title="matched → entered → guards passed → enqueued">
                          Funnel
                        </th>
                        <th className="px-3 py-2 font-medium text-right">Affected users</th>
                        <th className="px-3 py-2 font-medium">Duration</th>
                        <th className="px-3 py-2 font-medium">Error</th>
                      </tr>
                    </thead>
                    <tbody className={surfaces.primary}>
                      {runs.map((run) => (
                        <tr key={run.id} className={`border-t ${borders.light} align-top`}>
                          <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.primary} whitespace-nowrap max-w-[220px]`}>
                            <div className="truncate" title={run.automation?.name || ""}>
                              {run.automation?.name || (
                                <span className={textColors.muted}>Deleted automation</span>
                              )}
                            </div>
                            {run.automation?.slug ? (
                              <div className={`${typography.caption} ${textColors.tertiary} font-mono truncate`}>{run.automation.slug}</div>
                            ) : null}
                            {run.trigger_type === "event" ? (
                              <div className={`${typography.caption} ${textColors.secondary} truncate`} title={run.trigger_event || ""}>
                                Event · {run.trigger_event_label || run.trigger_event || "—"}
                              </div>
                            ) : null}
                          </td>
                          <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.primary} whitespace-nowrap`}>
                            <div>{formatRelativeTime(run.started_at)}</div>
                            <div className={`${typography.caption} ${textColors.tertiary}`}>{formatDateTime(run.started_at)}</div>
                          </td>
                          <td className="px-3 py-2">
                            <RunKindBadges run={run} />
                          </td>
                          <td className="px-3 py-2">
                            <RunFunnel counts={runToFunnelCounts(run)} mode="run" variant="compact" />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <AffectedUsersCell run={run} />
                          </td>
                          <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary} whitespace-nowrap`}>
                            <RunDuration run={run} />
                          </td>
                          <td className={`px-3 py-2 ${typography.caption} max-w-[220px]`}>
                            {run.error ? (
                              <span className="text-red-700 dark:text-red-300 break-words" title={run.error}>
                                {run.error}
                              </span>
                            ) : (
                              <span className={textColors.muted}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={total}
                count={runs.length}
                hasMore={hasMore}
                fetching={query.isFetching}
                onPage={(next) => setPage(Math.max(1, next))}
              />
            </>
          )}
        </CardBody>
      ) : null}
    </Card>
  );
};

AutomationRunHistorySection.propTypes = {
  automationOptions: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), name: PropTypes.string, slug: PropTypes.string })
  ),
  defaultOpen: PropTypes.bool,
  className: PropTypes.string,
};

export default AutomationRunHistorySection;
